import React, { useCallback, useEffect, useMemo } from 'react';
import { ATMMessage } from 'shared-it-appmod-ui';
import { FieldErrors } from 'react-hook-form';
import Lang from 'src/libraries/language';
import styles from './form-error.module.scss';

type IProps = {
  errors: FieldErrors;
  dictionary?: string;
};

type IError = {
  name: string;
  label: string;
  message: string;
};

const exceptionList: string[] = ['flightAreaFiles'];
const getErrors = (errors, parent = '') => {
  let list: IError[] = [];

  if (!errors) {
    return list;
  }

  Object.entries(errors).forEach(([key, value]: any) => {
    const name = `${parent}${
      /^\d+$/.test(key) ? `[${key}]` : `${parent ? '.' : ''}${key}`
    }`;
    const exception = parent.split('.')[1];
    if (typeof value.message === 'string') {
      list = [
        ...list,
        {
          name,
          label: (Lang.FORM_VALIDATION[key] ?? key).replace('?', ''),
          message: value.message,
        },
      ];
    } else if (exceptionList.includes(exception)) {
      list = [
        ...list,
        {
          name: exception,
          label: (Lang.FORM_VALIDATION[exception] ?? exception).replace(
            '?',
            ''
          ),
          message: (
            Lang.FORM_VALIDATION_Message[exception] ?? value.message
          ).replace('?', ''),
        },
      ];
    } else if (typeof value === 'object') {
      list = [...list, ...getErrors(value, name)];
    }
  });

  return list;
};

const FormError: React.FC<IProps> = ({ errors, dictionary = 'FORM_LABEL' }) => {
  const list = useMemo(() => getErrors(errors), [errors]);

  useEffect(() => {
    if (list.length) {
      window.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }
  }, [list]);

  const handleClick = useCallback((param: IError) => {
    const rawName = String(param.name || '');
    const dotName = rawName.replace(/\[(\d+)\]/g, '.$1');
    const bracketName = rawName.replace(/\.([0-9]+)(?=\.|$)/g, '[$1]');

    const candidates = Array.from(new Set([rawName, dotName, bracketName]));

    const findByName = (name: string): HTMLElement | null => {
      const exactMatches = document.getElementsByName(name);

      if (exactMatches.length) {
        return exactMatches[0];
      }

      const namedElements = document.querySelectorAll<HTMLElement>('[name]');

      for (let i = 0; i < namedElements.length; i += 1) {
        const value = namedElements[i].getAttribute('name') || '';

        if (value.startsWith(`${name}.`) || value.startsWith(`${name}[`)) {
          return namedElements[i];
        }
      }

      return null;
    };

    let element: HTMLElement | null = null;

    for (let i = 0; i < candidates.length; i += 1) {
      const byId = document.getElementById(candidates[i]);

      if (byId) {
        element = byId;
        break;
      }

      element = findByName(candidates[i]);

      if (element) {
        break;
      }
    }

    if (!element) {
      return;
    }

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    if (typeof element.focus === 'function') {
      element.focus();
    }
  }, []);

  return (
    <ATMMessage className={styles.wrapper} visible={!!list.length} error>
      <ATMMessage.Header>{Lang.TTL_FORM_VALIDATION_ERROR}</ATMMessage.Header>
      <ATMMessage.List>
        {list.map((value, key) => (
          <ATMMessage.Item key={key} onClick={() => handleClick(value)}>
            <strong>{`${
              Lang[dictionary][value.label] ?? value.label
            }: `}</strong>
            {value.message ?? ''}
          </ATMMessage.Item>
        ))}
      </ATMMessage.List>
    </ATMMessage>
  );
};

export default FormError;
